import { useState, useEffect } from 'react'
import { ChevronRight, X, Send, Plus, ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import Layout from '../../components/Layout/Layout'

const API_URL = import.meta.env.VITE_API_BASE_URL
    ? `${import.meta.env.VITE_API_BASE_URL}/api/support`
    : `${window.location.origin}/api/support`

type TicketStatus = 'open' | 'in_progress' | 'closed'
interface Ticket {
    _id: string
    ticketNumber: string
    subject: string
    category: string
    status: TicketStatus
    priority: string
    createdAt: string
    messages?: Message[]
}
interface Message {
    _id: string
    senderModel: 'User' | 'Admin'
    message: string
    createdAt: string
}

export default function SupportTicket() {
    const { user } = useAuth() as any
    const navigate = useNavigate()
    const userInfo = localStorage.getItem('userInfo')
    const token = userInfo ? JSON.parse(userInfo).token : null
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }

    const [tickets, setTickets] = useState<Ticket[]>([])
    const [selected, setSelected] = useState<Ticket | null>(null)
    const [creating, setCreating] = useState(false)
    const [subject, setSubject] = useState('')
    const [category, setCategory] = useState('general')
    const [message, setMessage] = useState('')
    const [reply, setReply] = useState('')
    const [loading, setLoading] = useState(false)

    const fetchTickets = async () => {
        const res = await fetch(`${API_URL}/tickets`, { headers })
        const data = await res.json()
        if (data.success) setTickets(data.data)
    }

    const fetchTicket = async (id: string) => {
        const res = await fetch(`${API_URL}/tickets/${id}`, { headers })
        const data = await res.json()
        if (data.success) setSelected(data.data)
    }

    useEffect(() => { fetchTickets() }, [])

    const handleCreate = async () => {
        if (!subject || !message) return
        setLoading(true)
        const res = await fetch(`${API_URL}/tickets`, { method: 'POST', headers, body: JSON.stringify({ subject, category, message }) })
        const data = await res.json()
        if (data.success) { setCreating(false); setSubject(''); setMessage(''); fetchTickets() }
        setLoading(false)
    }

    const handleReply = async () => {
        if (!reply || !selected) return
        setLoading(true)
        const res = await fetch(`${API_URL}/tickets/${selected._id}/reply`, { method: 'POST', headers, body: JSON.stringify({ message: reply }) })
        const data = await res.json()
        if (data.success) { setReply(''); fetchTicket(selected._id) }
        setLoading(false)
    }

    const statusColor: Record<TicketStatus, string> = { open: '#1CD4A7', in_progress: '#F59E0B', closed: '#71717A' }

    if (selected) return (
        <Layout activePage="support" hideMobileNav={true} hideFooterMobile={true}>
            <div style={{ minHeight: '100vh', background: 'var(--ui-bg-primary)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px 16px', borderBottom: '1px solid #1C1C2C' }}>
                    <button onClick={() => setSelected(null)} style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--ui-text-primary)', flexShrink: 0 }}>
                        <ArrowLeft size={18} />
                    </button>
                    <h1 style={{ fontSize: 17, fontWeight: 700, color: 'var(--ui-text-primary)', margin: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.subject}</h1>
                    <span style={{ fontSize: 12, color: statusColor[selected.status as TicketStatus], background: 'rgba(28,212,167,0.1)', padding: '3px 10px', borderRadius: 6, flexShrink: 0 }}>{selected.status.replace('_', ' ')}</span>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {selected.messages?.map(m => (
                        <div key={m._id} style={{ display: 'flex', justifyContent: m.senderModel === 'User' ? 'flex-end' : 'flex-start' }}>
                            <div style={{ maxWidth: '75%', background: m.senderModel === 'User' ? '#1CD4A7' : '#13121A', color: m.senderModel === 'User' ? '#000' : '#fff', borderRadius: m.senderModel === 'User' ? '12px 0 12px 12px' : '0 12px 12px 12px', padding: '10px 14px', fontSize: 14, border: m.senderModel !== 'User' ? '1px solid #1C1C2C' : 'none' }}>
                                {m.message}
                                <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4 }}>{new Date(m.createdAt).toLocaleString()}</div>
                            </div>
                        </div>
                    ))}
                </div>
                {selected.status !== 'closed' && (
                    <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderTop: '1px solid #1C1C2C' }}>
                        <input value={reply} onChange={e => setReply(e.target.value)} placeholder="Type a reply..." style={{ flex: 1, background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 10, padding: '10px 14px', color: 'var(--ui-text-primary)', fontSize: 14, fontFamily: 'inherit' }} onKeyDown={e => e.key === 'Enter' && handleReply()} />
                        <button onClick={handleReply} disabled={loading} style={{ background: '#1CD4A7', border: 'none', borderRadius: 10, padding: '0 18px', cursor: 'pointer', flexShrink: 0 }}><Send size={18} color="#000" /></button>
                    </div>
                )}
            </div>
        </Layout>
    )

    if (creating) return (
        <Layout activePage="support" hideMobileNav={true} hideFooterMobile={true}>
            <div style={{ minHeight: '100vh', background: 'var(--ui-bg-primary)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px 16px', borderBottom: '1px solid #1C1C2C' }}>
                    <button onClick={() => setCreating(false)} style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--ui-text-primary)', flexShrink: 0 }}>
                        <ArrowLeft size={18} />
                    </button>
                    <h1 style={{ fontSize: 17, fontWeight: 700, color: 'var(--ui-text-primary)', margin: 0 }}>New Support Ticket</h1>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                    <div>
                        <label style={{ color: 'var(--ui-text-secondary)', fontSize: 13, marginBottom: 6, display: 'block' }}>Subject</label>
                        <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Describe your issue briefly" style={{ width: '100%', background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 10, padding: '12px 14px', color: 'var(--ui-text-primary)', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' }} />
                    </div>
                    <div>
                        <label style={{ color: 'var(--ui-text-secondary)', fontSize: 13, marginBottom: 6, display: 'block' }}>Category</label>
                        <select value={category} onChange={e => setCategory(e.target.value)} style={{ width: '100%', background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 10, padding: '12px 14px', color: 'var(--ui-text-primary)', fontSize: 14, fontFamily: 'inherit' }}>
                            {['general','deposit','withdrawal','trading','p2p','kyc','security','other'].map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ color: 'var(--ui-text-secondary)', fontSize: 13, marginBottom: 6, display: 'block' }}>Message</label>
                        <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe your issue in detail..." rows={6} style={{ width: '100%', background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 10, padding: '12px 14px', color: 'var(--ui-text-primary)', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                    </div>
                    <button onClick={handleCreate} disabled={loading || !subject || !message} style={{ background: '#1CD4A7', color: '#000', border: 'none', borderRadius: 12, padding: '15px', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: (!subject || !message) ? 0.5 : 1, fontFamily: 'inherit' }}>
                        {loading ? 'Submitting...' : 'Submit Ticket'}
                    </button>
                </div>
            </div>
        </Layout>
    )

    return (
        <Layout activePage="support" hideMobileNav={true} hideFooterMobile={true}>
            <div style={{ minHeight: '100vh', background: 'var(--ui-bg-primary)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px 16px', borderBottom: '1px solid #1C1C2C' }}>
                    <button onClick={() => navigate(-1)} style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--ui-text-primary)', flexShrink: 0 }}>
                        <ArrowLeft size={18} />
                    </button>
                    <h1 style={{ fontSize: 17, fontWeight: 700, color: 'var(--ui-text-primary)', margin: 0, flex: 1 }}>Support Tickets</h1>
                    <button onClick={() => setCreating(true)} style={{ background: '#1CD4A7', color: '#000', border: 'none', borderRadius: 10, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, fontFamily: 'inherit' }}>
                        <Plus size={15} /> New
                    </button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', maxWidth: 700, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                    {tickets.length === 0 ? (
                        <div style={{ textAlign: 'center', color: 'var(--ui-text-muted)', padding: 48 }}>No tickets yet. Create one if you need help.</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {tickets.map(t => (
                                <div key={t._id} onClick={() => fetchTicket(t._id)} style={{ background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 14, padding: '14px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ color: 'var(--ui-text-primary)', fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</div>
                                        <div style={{ color: 'var(--ui-text-muted)', fontSize: 12, marginTop: 4 }}>#{t.ticketNumber} · {t.category} · {new Date(t.createdAt).toLocaleDateString()}</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 12 }}>
                                        <span style={{ fontSize: 12, color: statusColor[t.status as TicketStatus], background: 'rgba(28,212,167,0.08)', padding: '3px 10px', borderRadius: 6 }}>{t.status.replace('_',' ')}</span>
                                        <ChevronRight size={16} color="#71717A" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    )
}
